import { type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { ScrollableList } from '@/components/ui/ScrollableList'
import { morphToast } from '@/components/ui/Toast'
import { ALL_DIALECTS } from '@/database/dialectRegistry'
import { useImportActions } from '@/hooks/useImportActions'
import { useModalStore } from '@/hooks/useModalStore'
import type {
  ChangedColumnDiff,
  ChangedIndexDiff,
  ChangedRelationshipDiff,
  ChangedTableDiff,
  IncrementalImportOptions,
  IncrementalImportResult,
} from '@/services/incrementalImportService'
import { applyImportLayout, type SqlDialect } from '@/services/sqlImportService'
import { useDiagramStore } from '@/stores'
import { projectApi } from '@/api/projectApi'
import { createCommitFromMetadata } from '@/services/projectBootstrap'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { navigateOrShowTabLimit } from '@/stores/tabLimitStore'
import { emptyMetadataV1, type MetadataColumn, type MetadataTable, type MetadataRelationship, type ProjectMetadataV1 } from '@/services/metadata/schema'
import { openPlanDialogForError } from '@/utils/openPlanDialogForError'
import type { Column, DatabaseDialect, Relation, Table } from '@/types'

type ImportSummary = ReturnType<ReturnType<typeof useImportActions>['previewIncrementalSqlImport']>['summary']

function readChangeValue(record: object, key: string) {
  return String((record as Record<string, unknown>)[key] ?? 'null')
}

function SummaryPanel({ t, summary }: { t: ReturnType<typeof useTranslation>['t']; summary: ImportSummary }) {
  return (
    <div className="rounded-xl border px-4 py-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
      <div className="mb-3 text-xs font-semibold">{t('import.sqlSummaryTitle')}</div>
      <div className="grid gap-3 md:grid-cols-4 text-[11px]">
        <div>{t('import.sqlSummaryTables')}: {summary.tableCount}</div>
        <div>{t('import.sqlSummaryColumns')}: {summary.columnCount}</div>
        <div>{t('import.sqlSummaryPrimaryKeys')}: {summary.primaryKeyCount}</div>
        <div>{t('import.sqlSummaryForeignKeys')}: {summary.foreignKeyCount}</div>
        <div>{t('import.sqlSummaryIndexes')}: {summary.indexCount}</div>
        <div>{t('import.sqlSummaryComments')}: {summary.commentCount}</div>
        <div>{t('import.sqlSummaryWarnings')}: {summary.warningCount}</div>
        <div>{t('import.sqlSummaryIgnored')}: {summary.ignoredCommandCount}</div>
      </div>
      <div className="mt-4 text-[11px]" style={{ color: 'var(--text-dim)' }}>
        {t('import.sqlSummarySchemas')}: {summary.schemas.length ? summary.schemas.join(', ') : '—'}
      </div>
    </div>
  )
}

function RelationshipLabel({ value }: { value: { constraintName?: string; fromTableName?: string; from?: string; fromColumn?: string; col?: string; toTableName?: string; to?: string; toColumn?: string } }) {
  const sourceTable = value.fromTableName || value.from || '—'
  const sourceColumn = value.fromColumn || value.col || '—'
  const targetTable = value.toTableName || value.to || '—'
  const targetColumn = value.toColumn || 'id'
  const prefix = value.constraintName ? `${value.constraintName}: ` : ''

  return <>{prefix}{sourceTable}.{sourceColumn} → {targetTable}.{targetColumn}</>
}

function ChangeList({ changes }: { changes: Array<{ label: string; before: string; after: string }> }) {
  if (!changes.length) return null

  return (
    <ul className="mt-1 space-y-1 pl-4">
      {changes.map((change) => (
        <li key={`${change.label}-${change.before}-${change.after}`} className="list-disc">
          {change.label}: {change.before || 'null'} → {change.after || 'null'}
        </li>
      ))}
    </ul>
  )
}

function mapColumnChanges(column: ChangedColumnDiff) {
  return column.changes.map((change) => {
    if (change === 'type') return { label: 'type', before: column.before.type, after: column.after.type }
    if (change === 'nullable') return { label: 'nullable', before: String(!column.before.notNull), after: String(!column.after.notNull) }
    if (change === 'blank') return { label: 'blank', before: String(column.before.blank), after: String(column.after.blank) }
    if (change === 'desc') return { label: 'description', before: column.before.desc || 'null', after: column.after.desc || 'null' }
    if (change === 'logicalType') return { label: 'logicalType', before: column.before.logicalType || 'null', after: column.after.logicalType || 'null' }
    if (change === 'key') return { label: 'key', before: column.before.key || 'null', after: column.after.key || 'null' }
    if (change === 'primaryKey') return { label: 'primary key', before: String(!!column.before.pk), after: String(!!column.after.pk) }
    if (change === 'foreignKey') return { label: 'foreign key', before: String(!!column.before.fk), after: String(!!column.after.fk) }
    if (change === 'unique') return { label: 'unique', before: String(!!column.before.uq), after: String(!!column.after.uq) }
    return { label: change, before: readChangeValue(column.before, change), after: readChangeValue(column.after, change) }
  })
}

function mapIndexChanges(index: ChangedIndexDiff) {
  return index.changes.map((change) => {
    if (change === 'columns') return { label: 'columns', before: index.before.cols.join(', '), after: index.after.cols.join(', ') }
    if (change === 'unique') return { label: 'unique', before: String(index.before.unique), after: String(index.after.unique) }
    if (change === 'type') return { label: 'type', before: index.before.type, after: index.after.type }
    return { label: change, before: readChangeValue(index.before, change), after: readChangeValue(index.after, change) }
  })
}

function mapTableChanges(table: ChangedTableDiff) {
  return table.changes.map((change) => ({
    label: change,
    before: readChangeValue(table.before, change),
    after: readChangeValue(table.after, change),
  }))
}

function mapRelationshipChanges(relationship: ChangedRelationshipDiff) {
  return relationship.changes.map((change) => {
    if (change === 'fromColumn') return { label: 'origem', before: relationship.before.fromColumn || relationship.before.col, after: relationship.after.fromColumn || relationship.after.col }
    if (change === 'toColumn') return { label: 'destino', before: relationship.before.toColumn || 'id', after: relationship.after.toColumn || 'id' }
    if (change === 'fromTable') return { label: 'tabela origem', before: relationship.before.fromTableName || relationship.before.from, after: relationship.after.fromTableName || relationship.after.from }
    if (change === 'toTable') return { label: 'tabela destino', before: relationship.before.toTableName || relationship.before.to, after: relationship.after.toTableName || relationship.after.to }
    return { label: change, before: readChangeValue(relationship.before, change), after: readChangeValue(relationship.after, change) }
  })
}

function SectionBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-sm font-semibold">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function CategoryBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>{title}</div>
      <div className="rounded-xl border px-3 py-3 text-[11px]" style={{ borderColor: 'var(--border)' }}>
        {children}
      </div>
    </div>
  )
}

function EmptyDash() {
  return <div style={{ color: 'var(--text-dim)' }}>—</div>
}

function DiffReport({ preview }: { preview: IncrementalImportResult }) {
  const { t } = useTranslation()

  return (
    <div className="space-y-6 rounded-xl border px-4 py-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
      <div className="text-xs font-semibold">{t('import.diffReportTitle')}</div>

      <SectionBlock title={t('import.diffAdded')}>
        <CategoryBlock title={t('import.sqlSummaryTables')}>
          {preview.diff.addedTables.length ? preview.diff.addedTables.map((table) => (
            <div key={table.id}>- {table.fullName}</div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffRelationships')}>
          {preview.diff.addedRelationships.length ? preview.diff.addedRelationships.map((relationship) => (
            <div key={relationship.id}>- <RelationshipLabel value={relationship} /></div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.sqlSummaryColumns')}>
          {preview.diff.addedColumns.length ? preview.diff.addedColumns.map((column) => (
            <div key={`${column.tableName}.${column.columnName}`}>- {column.tableName}.{column.columnName}</div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffIndexes')}>
          {preview.diff.addedIndexes.length ? preview.diff.addedIndexes.map((index) => (
            <div key={`${index.tableName}.${index.indexName}`}>- {index.indexName} ({index.tableName})</div>
          )) : <EmptyDash />}
        </CategoryBlock>
      </SectionBlock>

      <SectionBlock title={t('import.diffModified')}>
        <CategoryBlock title={t('import.sqlSummaryTables')}>
          {preview.diff.modifiedTables.length ? preview.diff.modifiedTables.map((table) => (
            <div key={table.tableName}>
              - {table.tableName}
              <ChangeList changes={mapTableChanges(table)} />
            </div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffRelationships')}>
          {preview.diff.changedRelationships.length ? preview.diff.changedRelationships.map((relationship) => (
            <div key={relationship.before.id}>
              - <RelationshipLabel value={relationship.after} />
              <ChangeList changes={mapRelationshipChanges(relationship)} />
            </div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.sqlSummaryColumns')}>
          {preview.diff.changedColumns.length ? preview.diff.changedColumns.map((column) => (
            <div key={`${column.tableName}.${column.columnName}`}>
              - {column.tableName}.{column.columnName}
              <ChangeList changes={mapColumnChanges(column)} />
            </div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffIndexes')}>
          {preview.diff.changedIndexes.length ? preview.diff.changedIndexes.map((index) => (
            <div key={`${index.tableName}.${index.indexName}`}>
              - {index.indexName} ({index.tableName})
              <ChangeList changes={mapIndexChanges(index)} />
            </div>
          )) : <EmptyDash />}
        </CategoryBlock>
      </SectionBlock>

      <SectionBlock title={t('import.diffRemoved')}>
        <CategoryBlock title={t('import.sqlSummaryTables')}>
          {preview.diff.removedTables.length ? preview.diff.removedTables.map((table) => (
            <div key={table.id}>- {table.fullName}</div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffRelationships')}>
          {preview.diff.removedRelationships.length ? preview.diff.removedRelationships.map((relationship) => (
            <div key={relationship.id}>- <RelationshipLabel value={relationship} /></div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.sqlSummaryColumns')}>
          {preview.diff.removedColumns.length ? preview.diff.removedColumns.map((column) => (
            <div key={`${column.tableName}.${column.columnName}`}>- {column.tableName}.{column.columnName}</div>
          )) : <EmptyDash />}
        </CategoryBlock>
        <CategoryBlock title={t('import.diffIndexes')}>
          {preview.diff.removedIndexes.length ? preview.diff.removedIndexes.map((index) => (
            <div key={`${index.tableName}.${index.indexName}`}>- {index.indexName} ({index.tableName})</div>
          )) : <EmptyDash />}
        </CategoryBlock>
      </SectionBlock>

      <SectionBlock title={t('import.sqlSummaryWarnings')}>
        <CategoryBlock title={t('import.sqlSummaryWarnings')}>
          {preview.diff.warnings.length ? (
            <ScrollableList maxVisibleItems={7} itemHeight={28} className="space-y-2 pr-1">
              {preview.diff.warnings.map((warning, index) => (
                <div key={`${warning}-${index}`}>- {warning}</div>
              ))}
            </ScrollableList>
          ) : <EmptyDash />}
        </CategoryBlock>
      </SectionBlock>
    </div>
  )
}

type ImportMode = 'new' | 'update'

function columnToMeta(col: Column): MetadataColumn {
  return {
    id: col.id,
    key: col.key,
    pk: col.pk,
    fk: col.fk,
    uq: col.uq,
    name: col.name,
    type: col.type,
    logicalType: col.logicalType,
    notNull: col.notNull,
    blank: col.blank,
    desc: col.desc,
    defaultValue: col.defaultValue,
    checkExpression: col.checkExpression,
  }
}

function buildMetadataFromParsedSql(
  parsed: { tables: Table[]; rels: Relation[] },
  project: { name: string; description: string; dialect: DatabaseDialect },
): ProjectMetadataV1 {
  const base = emptyMetadataV1(project)
  const positions: Record<string, { x: number; y: number }> = {}
  const tables: MetadataTable[] = parsed.tables.map((t) => {
    positions[t.id] = { x: t.x, y: t.y }
    return {
      id: t.id,
      schema: t.schema,
      name: t.name,
      fullName: t.fullName,
      comment: t.comment,
      g: t.g,
      cols: t.cols.map(columnToMeta),
      indexes: t.indexes ?? [],
    }
  })
  const relationships: MetadataRelationship[] = parsed.rels.map((r) => ({
    id: r.id,
    from: r.from,
    to: r.to,
    col: r.col,
    type: r.type,
    mid: r.mid,
    fromAnchor: r.fromAnchor,
    toAnchor: r.toAnchor,
    waypoints: r.waypoints ?? [],
    isManual: r.isManual,
    labelT: r.labelT,
    constraintName: r.constraintName,
    relationshipKey: r.relationshipKey,
  }))
  return {
    ...base,
    tables,
    relationships,
    layout: { ...base.layout, positions },
  }
}

interface SqlImportModalProps {
  workspaceId?: number
}

function summarizeIgnoredEmptyTables(tableNames: string[]) {
  if (!tableNames.length) return null
  const preview = tableNames.slice(0, 3).join(', ')
  return tableNames.length > 3 ? `${preview} +${tableNames.length - 3}` : preview
}

export function SqlImportModal({ workspaceId }: SqlImportModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const sqlImport = useModalStore((state) => state.sqlImport)
  const close = useModalStore((state) => state.closeSqlImport)
  const { previewIncrementalSqlImport, applyIncrementalSqlImport, hasProjectContent } = useImportActions()
  const projectDialect = useDiagramStore((state) => state.dialect)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const insertProject = useProjectsStore((s) => s.insertProject)

  const [sql, setSql] = useState('')
  const [dialect, setDialect] = useState<SqlDialect>(projectDialect)
  const [errorMessage, setErrorMessage] = useState('')
  const [summary, setSummary] = useState<ImportSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const [preview, setPreview] = useState<(IncrementalImportResult & { summary: ImportSummary }) | null>(null)
  const [importMode, setImportMode] = useState<ImportMode>('update')
  const [incrementalOptions, setIncrementalOptions] = useState<IncrementalImportOptions>({
    removeMissingTables: true,
    removeMissingColumns: true,
    removeMissingRelationships: true,
    preserveTablePositions: true,
    preserveRelationshipRouting: true,
  })

  const hasContent = hasProjectContent()
  const canUpdateCurrentProject = sqlImport.source === 'editor-import' && hasContent
  const isEditorImport = sqlImport.source === 'editor-import'
  const dialectOptions = ALL_DIALECTS.map((item) => ({ value: item.value as SqlDialect, label: item.label }))
  const targetWorkspaceId = workspaceId ?? currentWorkspace?.id ?? null

  useEffect(() => {
    if (!sqlImport.open) return
    setDialect(projectDialect)
    setErrorMessage('')
    setSummary(null)
    setBusy(false)
    setIsDragActive(false)
    setPreview(null)
    setImportMode(canUpdateCurrentProject ? 'update' : 'new')
    setIncrementalOptions({
      removeMissingTables: true,
      removeMissingColumns: true,
      removeMissingRelationships: true,
      preserveTablePositions: true,
      preserveRelationshipRouting: true,
    })
  }, [canUpdateCurrentProject, projectDialect, sqlImport.open])

  const loadFile = async (file: File | null | undefined) => {
    if (!file) return
    setSql(await file.text())
    setPreview(null)
    setSummary(null)
  }

  const runImportAsNew = async () => {
    if (!sql.trim()) { setErrorMessage(t('import.pasteSql')); return }
    if (!targetWorkspaceId) { setErrorMessage(t('import.noWorkspace', 'Nenhum workspace ativo.')); return }
    setBusy(true)
    setErrorMessage('')
    let projectId: number | null = null
    try {
      const parsed = applyImportLayout(await projectApi.parseSql(targetWorkspaceId, { sql, dialect }))
      const validTables = parsed.tables.filter((table) => (table.cols?.length ?? 0) > 0)
      const ignoredEmptyTables = parsed.tables
        .filter((table) => (table.cols?.length ?? 0) === 0)
        .map((table) => table.fullName || table.name)
      if (ignoredEmptyTables.length) {
        morphToast(`Tabelas ignoradas sem colunas: ${summarizeIgnoredEmptyTables(ignoredEmptyTables)}`)
      }
      if (!validTables.length) {
        setErrorMessage(t('import.noCreateTable'))
        return
      }
      const dbDialect: DatabaseDialect =
        (dialect === 'generic' ? 'postgresql' : dialect) as DatabaseDialect
      const projectName = `SQL Import ${new Date().toLocaleString()}`
      const rels = parsed.rels.filter((rel) =>
        validTables.some((table) => table.id === rel.from) && validTables.some((table) => table.id === rel.to),
      )
      const meta = buildMetadataFromParsedSql({ tables: validTables, rels }, {
        name: projectName,
        description: '',
        dialect: dbDialect,
      })

      const project = await projectApi.create(targetWorkspaceId, { name: projectName, dialect: dbDialect }, { silent: true })
      projectId = project.id

      await createCommitFromMetadata(project.id, meta, {
        name: t('projects.initialCommit', 'Projeto criado'),
        description: t('import.sqlInitialDescription', 'Importado de SQL'),
      }, { silent: true })

      insertProject(project)
      morphToast.success(t('import.incrementalApplied'))
      close()
      navigateOrShowTabLimit(navigate, `/workspace/${currentWorkspace?.slug ?? ''}/projects/${project.id}/editor`)
    } catch (error) {
      if (projectId) {
        try { await projectApi.delete(projectId, { silent: true }) }
        catch {
          try { await projectApi.patch(projectId, { is_active: false }, { silent: true }) }
          catch { /* best-effort rollback */ }
        }
      }
      openPlanDialogForError(error)
      setErrorMessage(error instanceof Error ? error.message : t('import.importError', { message: t('common.unknown') }))
    } finally {
      setBusy(false)
    }
  }

  const runIncrementalPreview = async () => {
    if (!sql.trim()) { setErrorMessage(t('import.pasteSql')); return }
    setBusy(true)
    setErrorMessage('')
    try {
      const result = await previewIncrementalSqlImport({ sql, options: incrementalOptions })
      if (!result.summary.tableCount) {
        setErrorMessage(t('import.noCreateTable'))
        setSummary(result.summary)
        setPreview(null)
        return
      }
      setSummary(result.summary)
      setPreview(result)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('import.importError', { message: t('common.unknown') }))
    } finally {
      setBusy(false)
    }
  }

  const runApplyIncremental = () => {
    if (!preview) return
    setBusy(true)
    setErrorMessage('')
    try {
      applyIncrementalSqlImport(preview)
      morphToast.success(t('import.incrementalApplied'))
      close()
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('import.importError', { message: t('common.unknown') }))
    } finally {
      setBusy(false)
    }
  }

  const toggleIncrementalOption = (key: keyof IncrementalImportOptions) => {
    setIncrementalOptions((current) => ({ ...current, [key]: !current[key] }))
    setPreview(null)
  }

  const cancelPreview = () => {
    setPreview(null)
    close()
  }

  return (
    <ModalShell
      open={sqlImport.open}
      onClose={close}
      title={t('import.sqlModalTitle')}
      description={t('import.sqlModalDesc')}
      widthClassName="max-w-5xl"
      footer={(
        <>
          {importMode === 'new' ? (
            <>
              <Button variant="ghost" onClick={close}>{t('common.close')}</Button>
              <Button variant="primary" onClick={() => void runImportAsNew()} disabled={busy}>
                {busy ? '…' : t('import.importMenuNewProject')}
              </Button>
            </>
          ) : preview ? (
            <>
              <Button variant="ghost" onClick={cancelPreview}>{t('common.cancel')}</Button>
              <Button variant="primary" onClick={runApplyIncremental} disabled={busy}>{busy ? '…' : t('import.acceptChange')}</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>{t('common.close')}</Button>
              <Button variant="primary" onClick={() => void runIncrementalPreview()} disabled={busy}>
                {busy ? '…' : t('import.importMenuUpdateCurrent')}
              </Button>
            </>
          )}
        </>
      )}
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          {!isEditorImport ? (
            <button
              type="button"
              onClick={() => { setImportMode('new'); setPreview(null); setSummary(null) }}
              className="flex-1 rounded-xl border px-3 py-3 text-left text-[11px] transition-colors"
              style={{
                borderColor: importMode === 'new' ? 'rgba(99,102,241,.55)' : 'var(--border)',
                background: importMode === 'new' ? 'rgba(99,102,241,.08)' : 'var(--bg-card)',
              }}
            >
              <div className="font-semibold">{t('import.importMenuNewProject')}</div>
              <div className="mt-0.5" style={{ color: 'var(--text-dim)' }}>{t('import.importMenuNewProjectDesc')}</div>
            </button>
          ) : null}
          {canUpdateCurrentProject ? (
            <button
              type="button"
              onClick={() => { setImportMode('update'); setPreview(null); setSummary(null) }}
              className="flex-1 rounded-xl border px-3 py-3 text-left text-[11px] transition-colors"
              style={{
                borderColor: importMode === 'update' ? 'rgba(99,102,241,.55)' : 'var(--border)',
                background: importMode === 'update' ? 'rgba(99,102,241,.08)' : 'var(--bg-card)',
              }}
            >
              <div className="font-semibold">{t('import.importMenuUpdateCurrent')}</div>
              <div className="mt-0.5" style={{ color: 'var(--text-dim)' }}>{t('import.importMenuUpdateCurrentDesc')}</div>
            </button>
          ) : null}
        </div>

        {importMode === 'new' ? (
          <div className="max-w-[260px]">
            <MorphSelect
              ariaLabel={t('import.sqlDialectLabel')}
              value={dialect}
              options={dialectOptions}
              onChange={(nextDialect) => {
                if (!nextDialect) return
                setDialect(nextDialect)
                setPreview(null)
              }}
            />
          </div>
        ) : null}

        <label
          className="block cursor-pointer rounded-xl border border-dashed px-4 py-5 text-[11px] transition-colors"
          style={{
            borderColor: isDragActive ? 'rgba(99,102,241,.55)' : 'rgba(99,102,241,.28)',
            background: isDragActive ? 'rgba(99,102,241,.08)' : 'var(--bg-card)',
            color: 'var(--text-muted)',
          }}
          onDragOver={(event) => { event.preventDefault(); setIsDragActive(true) }}
          onDragLeave={(event) => { if (event.currentTarget.contains(event.relatedTarget as Node)) return; setIsDragActive(false) }}
          onDrop={(event) => { event.preventDefault(); setIsDragActive(false); void loadFile(event.dataTransfer.files?.[0]) }}
        >
          <div className="mb-2 text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
            {t('import.loadSql')}
          </div>
          <div className="text-[11px]">{t('import.sqlOpenFileHint')}</div>
          <input
            type="file"
            accept=".sql"
            className="hidden"
            onChange={(event) => { void loadFile(event.target.files?.[0]); event.target.value = '' }}
          />
        </label>

        <textarea
          value={sql}
          onChange={(event) => {
            setSql(event.target.value)
            setPreview(null)
          }}
          placeholder={t('import.sqlPlaceholder')}
          className="morph-textarea min-h-[320px]"
        />

        {canUpdateCurrentProject && importMode === 'update' && (
          <div className="rounded-xl border px-4 py-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
            <div className="mb-3 text-xs font-semibold">{t('import.incrementalTitle')}</div>
            <div className="grid gap-2 md:grid-cols-2 text-[11px]">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={incrementalOptions.removeMissingTables} onChange={() => toggleIncrementalOption('removeMissingTables')} className="morph-checkbox" />
                <span>{t('import.incrementalRemoveTables')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={incrementalOptions.removeMissingColumns} onChange={() => toggleIncrementalOption('removeMissingColumns')} className="morph-checkbox" />
                <span>{t('import.incrementalRemoveColumns')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={incrementalOptions.removeMissingRelationships} onChange={() => toggleIncrementalOption('removeMissingRelationships')} className="morph-checkbox" />
                <span>{t('import.incrementalRemoveRelationships')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={incrementalOptions.preserveRelationshipRouting} onChange={() => toggleIncrementalOption('preserveRelationshipRouting')} className="morph-checkbox" />
                <span>{t('import.incrementalPreserveRouting')}</span>
              </label>
            </div>
          </div>
        )}

        {errorMessage ? (
          <div className="rounded-xl border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
            {errorMessage}
          </div>
        ) : null}

        {canUpdateCurrentProject && importMode === 'update' && summary ? <SummaryPanel t={t} summary={summary} /> : null}
        {canUpdateCurrentProject && importMode === 'update' && preview ? <DiffReport preview={preview} /> : null}
      </div>
    </ModalShell>
  )
}
